import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  STEP_ACTIONS,
  STEP_TYPES,
  WORKFLOW_TRIGGERS,
  createWorkflow,
  createWorkflowStep,
  deleteWorkflow,
  deleteWorkflowStep,
  listWorkflowRuns,
  listWorkflowSteps,
  listWorkflows,
  runWorkflow,
  stepActionMeta,
  stepTypeMeta,
  updateWorkflow,
  workflowStatusMeta,
  workflowTriggerMeta,
  type CoreWorkflow,
  type CoreWorkflowRun,
  type CoreWorkflowStep,
} from '@/lib/coreWorkflow';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function WorkflowsTab({ org }: Props) {
  const [workflows, setWorkflows] = useState<CoreWorkflow[]>([]);
  const [selected, setSelected] = useState<CoreWorkflow | null>(null);
  const [steps, setSteps] = useState<CoreWorkflowStep[]>([]);
  const [runs, setRuns] = useState<CoreWorkflowRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wfOpen, setWfOpen] = useState(false);
  const [wfForm, setWfForm] = useState({ name: '', description: '', triggerEvent: 'appointment.confirmed' });
  const [stepOpen, setStepOpen] = useState(false);
  const [stepForm, setStepForm] = useState({ stepType: 'action', action: 'send_notification', label: '', delayHours: 0 });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listWorkflows(org.id);
      setWorkflows(list);
      if (list.length > 0) {
        const current = selected ? list.find((w) => w.id === selected.id) || list[0] : list[0];
        setSelected(current);
      } else {
        setSelected(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id, selected]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (wf: CoreWorkflow) => {
    const [s, r] = await Promise.all([listWorkflowSteps(wf.id), listWorkflowRuns(org.id, wf.id)]);
    setSteps(s);
    setRuns(r);
  }, [org.id]);

  useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);

  const createWf = async () => {
    if (!wfForm.name.trim()) return;
    setSaving(true);
    try {
      const id = await createWorkflow({ tenantId: org.tenant_id, organizationId: org.id, name: wfForm.name.trim(), description: wfForm.description.trim(), triggerEvent: wfForm.triggerEvent });
      setWfOpen(false);
      setWfForm({ name: '', description: '', triggerEvent: 'appointment.confirmed' });
      await load();
      if (id) {
        const fresh = (await listWorkflows(org.id)).find((w) => w.id === id) || null;
        setSelected(fresh);
      }
    } finally {
      setSaving(false);
    }
  };

  const addStep = async () => {
    if (!selected || !stepForm.label.trim()) return;
    setSaving(true);
    try {
      await createWorkflowStep({
        workflowId: selected.id,
        sortOrder: steps.length,
        stepType: stepForm.stepType,
        action: stepForm.stepType === 'action' || stepForm.stepType === 'notify' ? stepForm.action : null,
        label: stepForm.label.trim(),
        delayHours: stepForm.stepType === 'wait' ? stepForm.delayHours : null,
      });
      setStepOpen(false);
      setStepForm({ stepType: 'action', action: 'send_notification', label: '', delayHours: 0 });
      loadDetail(selected);
    } finally {
      setSaving(false);
    }
  };

  const removeStep = async (s: CoreWorkflowStep) => {
    setBusyId(s.id);
    try { await deleteWorkflowStep(s.id); if (selected) loadDetail(selected); } finally { setBusyId(null); }
  };

  const changeStatus = async (status: string) => {
    if (!selected) return;
    await updateWorkflow(selected.id, { status });
    await load();
  };

  const removeWf = async (wf: CoreWorkflow) => {
    setBusyId(wf.id);
    try { await deleteWorkflow(wf.id); setSelected(null); await load(); } finally { setBusyId(null); }
  };

  const doRun = async () => {
    if (!selected) return;
    setBusyId(selected.id);
    try { await runWorkflow({ workflowId: selected.id, organizationId: org.id, triggerEvent: selected.trigger_event || 'manual' }); loadDetail(selected); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-3xl">
          Workflow Engine : une infrastructure générique de workflows. Les règles spécifiques sont configurées ici, le moteur reste commun
          (ex. <span className="font-semibold text-foreground-700">RDV confirmé → notification → rappel → demande d’avis</span>).
        </p>
        <button onClick={() => setWfOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"><i className="ri-add-line"></i> Nouveau workflow</button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : workflows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-flow-chart text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucun workflow. Créez-en un pour automatiser vos scénarios.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
          <div className="space-y-2">
            {workflows.map((wf) => {
              const st = workflowStatusMeta(wf.status);
              return (
                <button key={wf.id} onClick={() => setSelected(wf)} className={`w-full text-left bg-background-50 border rounded-lg p-3 transition-colors cursor-pointer ${selected?.id === wf.id ? 'border-primary-300 ring-1 ring-primary-200' : 'border-background-200/70 hover:bg-background-100'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground-900 truncate">{wf.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${st.className}`}>{st.label}</span>
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-1 truncate">Déclencheur : {workflowTriggerMeta(wf.trigger_event).label}</p>
                </button>
              );
            })}
          </div>

          {selected && (
            <div className="space-y-4">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="text-base font-semibold text-foreground-900">{selected.name}</h4>
                    {selected.description && <p className="text-xs text-foreground-500 mt-0.5">{selected.description}</p>}
                    <p className="text-[11px] text-foreground-400 mt-1"><i className="ri-flashlight-line"></i> {workflowTriggerMeta(selected.trigger_event).label}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={doRun} disabled={busyId === selected.id} className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-play-line"></i> Simuler</button>
                    <select className={INPUT + ' !w-auto'} value={selected.status} onChange={(e) => changeStatus(e.target.value)}>
                      <option value="draft">Brouillon</option>
                      <option value="active">Actif</option>
                      <option value="paused">En pause</option>
                    </select>
                    <button onClick={() => removeWf(selected)} className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"><i className="ri-delete-bin-line"></i></button>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2"><span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-flow-chart"></i></span><h4 className="text-sm font-semibold text-foreground-900">Étapes du workflow</h4></div>
                  <button onClick={() => setStepOpen(true)} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-add-line"></i> Étape</button>
                </div>
                {steps.length === 0 ? (
                  <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune étape. Ajoutez-en pour construire le scénario.</p>
                ) : (
                  <div className="p-4 space-y-2">
                    {steps.map((s, i) => {
                      const t = stepTypeMeta(s.step_type);
                      return (
                        <div key={s.id} className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-background-100 flex items-center justify-center text-[11px] font-bold text-foreground-500 flex-shrink-0">{i + 1}</span>
                          <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border ${t.className} border-transparent`}>
                            <i className={t.icon}></i>
                            <span className="text-xs font-medium truncate">{s.label}</span>
                            {s.action && s.step_type !== 'wait' && <span className="text-[10px] opacity-70 truncate">· {stepActionMeta(s.action).label}</span>}
                            {s.step_type === 'wait' && <span className="text-[10px] opacity-70">· {s.delay_hours || 0} h</span>}
                          </div>
                          <button onClick={() => removeStep(s)} disabled={busyId === s.id} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50 flex-shrink-0"><i className="ri-delete-bin-line"></i></button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2"><span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-history-line"></i></span><h4 className="text-sm font-semibold text-foreground-900">Exécutions récentes</h4></div>
                {runs.length === 0 ? (
                  <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune exécution. Cliquez sur « Simuler » pour tester le scénario.</p>
                ) : (
                  <div className="divide-y divide-background-200/60">
                    {runs.map((r) => (
                      <div key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.status === 'completed' ? 'bg-primary-500' : r.status === 'failed' ? 'bg-red-500' : 'bg-accent-500'}`}></span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-foreground-800 truncate">Déclenché par {workflowTriggerMeta(r.trigger_event).label}</p>
                          <p className="text-[11px] text-foreground-400 truncate">{r.created_at ? new Date(r.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''} · {Array.isArray(r.log) ? `${r.log.length} étape(s)` : 'journal'}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${r.status === 'completed' ? 'bg-primary-100 text-primary-900' : 'bg-background-100 text-foreground-600'}`}>{r.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {wfOpen && (
        <Modal
          title="Nouveau workflow"
          onClose={() => setWfOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setWfOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={createWf} disabled={saving || !wfForm.name.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Créer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom</span>
              <input className={INPUT} value={wfForm.name} onChange={(e) => setWfForm({ ...wfForm, name: e.target.value })} placeholder="Ex : Après un rendez-vous" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Description (optionnel)</span>
              <input className={INPUT} value={wfForm.description} onChange={(e) => setWfForm({ ...wfForm, description: e.target.value })} placeholder="Objectif du scénario" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Déclencheur</span>
              <select className={INPUT} value={wfForm.triggerEvent} onChange={(e) => setWfForm({ ...wfForm, triggerEvent: e.target.value })}>
                {WORKFLOW_TRIGGERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
          </div>
        </Modal>
      )}

      {stepOpen && (
        <Modal
          title="Nouvelle étape"
          onClose={() => setStepOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setStepOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={addStep} disabled={saving || !stepForm.label.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Ajouter
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Type d’étape</span>
              <select className={INPUT} value={stepForm.stepType} onChange={(e) => setStepForm({ ...stepForm, stepType: e.target.value })}>
                {STEP_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            {(stepForm.stepType === 'action' || stepForm.stepType === 'notify') && (
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Action</span>
                <select className={INPUT} value={stepForm.action} onChange={(e) => setStepForm({ ...stepForm, action: e.target.value })}>
                  {STEP_ACTIONS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                </select>
              </label>
            )}
            {stepForm.stepType === 'wait' && (
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Attente (heures)</span>
                <input type="number" min={0} className={INPUT} value={stepForm.delayHours} onChange={(e) => setStepForm({ ...stepForm, delayHours: Number(e.target.value) || 0 })} />
              </label>
            )}
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Libellé</span>
              <input className={INPUT} value={stepForm.label} onChange={(e) => setStepForm({ ...stepForm, label: e.target.value })} placeholder="Ex : Envoyer une notification" />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}